import React from 'react'
import styled from '@emotion/styled'
import { Button } from '../../components'
import { ISubscription } from '../../api/controllers/subscriptions.controller'

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #21304e;
`

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
  background: ${(props) => {
    if (props.status === 'active' || props.status === 'trialing') {
      return '#4caf50'
    }
    if (props.status === 'canceled' || props.status === 'past_due') {
      return '#f44336'
    }
    return '#ff9800'
  }};
  color: white;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`

const InfoLabel = styled.span`
  font-size: 16px;
  color: #737991;
`

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #21304e;
`

const ButtonContainer = styled.div`
  margin-top: 30px;
  text-align: center;
`

const StyledButton = styled(Button)`
  max-width: 100% !important;
`

const NoSubscriptionMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #737991;
  font-size: 16px;
  line-height: 1.6;
`

const Link = styled.a`
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`

interface ISubscriptionSectionProps {
  subscription: ISubscription | null
  interviewCount: number
  remainingInterviews: number
  onManageSubscription: () => void
  loading?: boolean
}

const SubscriptionSection: React.FC<ISubscriptionSectionProps> = ({
  subscription,
  interviewCount,
  remainingInterviews,
  onManageSubscription,
  loading = false,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active'
      case 'trialing':
        return 'Trial'
      case 'canceled':
        return 'Canceled'
      case 'past_due':
        return 'Past Due'
      case 'incomplete':
        return 'Incomplete'
      case 'incomplete_expired':
        return 'Incomplete Expired'
      case 'unpaid':
        return 'Unpaid'
      default:
        return status.charAt(0).toUpperCase() + status.slice(1)
    }
  }

  const hasActiveSubscription = subscription && (subscription.status === 'active' || subscription.status === 'trialing')

  if (!subscription) {
    return (
      <Section>
        <SectionTitle>Subscription</SectionTitle>
        <NoSubscriptionMessage>
          <p>You don't have an active subscription.</p>
          <p>
            You have access to 1 free interview per month. To get unlimited
            interviews, please{' '}
            <Link href="/pricing">subscribe to a plan</Link>.
          </p>
        </NoSubscriptionMessage>
      </Section>
    )
  }

  return (
    <Section>
      <SectionTitle>Subscription</SectionTitle>
      <StatusBadge status={subscription.status}>
        {getStatusLabel(subscription.status)}
        {subscription.cancel_at_period_end && subscription.status === 'active' && ' (Canceling)'}
      </StatusBadge>

      {subscription.current_period_start && subscription.current_period_end && (
        <>
          <InfoRow>
            <InfoLabel>Current Period</InfoLabel>
            <InfoValue>
              {formatDate(subscription.current_period_start)} -{' '}
              {formatDate(subscription.current_period_end)}
            </InfoValue>
          </InfoRow>
        </>
      )}

      <InfoRow>
        <InfoLabel>Interviews Used This Month</InfoLabel>
        <InfoValue>{interviewCount}</InfoValue>
      </InfoRow>

      <InfoRow>
        <InfoLabel>Remaining Interviews</InfoLabel>
        <InfoValue>{remainingInterviews}</InfoValue>
      </InfoRow>

      {hasActiveSubscription && (
        <ButtonContainer>
          <StyledButton
            title="Manage Subscription"
            onClick={onManageSubscription}
            disabled={loading}
          />
        </ButtonContainer>
      )}
    </Section>
  )
}

export default SubscriptionSection
